import React, { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import FailureModal from "../../../../Components/Modal/supplier_app/FailureModal";

// ─── Constants ────────────────────────────────────────────────────────────────

const RATE_SLABS = [
    { label: "₹2.8 / km", value: 2.8, type: "fixed" },
    { label: "₹4 / km", value: 4, type: "fixed" },
    { label: "Manual", value: "", type: "manual" },
];

const TRIP_TYPES = ["Dispatch", "Installation", "Return", "Service", "Collect Money"];

const tableHeadStyle = {
    headRow: { style: { backgroundColor: "#ebf0f5", fontWeight: "bold" } },
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const calcTotal = (rate, km) => {
    const r = parseFloat(rate) || 0;
    const k = parseFloat(km) || 0;
    return parseFloat((r * k).toFixed(2));
};

// Stage keys: "se" → "dispatch" → "finance" → null (complete)
const getNextStage = (row) => {
    if (!row.seConfirmation?.confirmed) return "se";
    if (!row.dispatchConfirmation?.confirmed) return "dispatch";
    if (!row.financeConfirmation?.confirmed) return "finance";
    return null;
};

const getStatusBadge = (row) => {
    if (row.local_id)
        return <span className="badge bg-secondary">Not saved</span>;
    if (row.financeConfirmation?.confirmed)
        return <span className="badge bg-success">✓ Paid</span>;
    if (row.dispatchConfirmation?.confirmed)
        return (
            <span className="badge bg-warning text-dark">Pending Finance</span>
        );
    if (row.seConfirmation?.confirmed)
        return (
            <span className="badge bg-info text-dark">Pending Dispatch</span>
        );
    return <span className="badge bg-secondary">Pending SE</span>;
};

// Plain text cell — no disabled input boxes
const ReadCell = ({ value }) => (
    <div className="commTopButtonRightLeftPadding">
        <span>{value ?? "—"}</span>
    </div>
);

// ─── Component ────────────────────────────────────────────────────────────────
const ERFTravelSection = ({
    travelHistory = [],
    erfStatusCode = "",
    onSave,
    onConfirmRow,
    tripType = "",
    disabled = false,
    onUnsavedRowsChange
}) => {
    // ── Roles from Redux ────────────────────────────────────────────────────────
    const userData = useSelector((state) => state?.user);

    const [isServiceExecutive, setIsServiceExecutive] = useState(false);
    const [isDispatchManager, setIsDispatchManager] = useState(false);
    const [isFinanceExecutive, setIsFinanceExecutive] = useState(false);
    const [currentUserName, setCurrentUserName] = useState("");

    useEffect(() => {
        if (userData?.data?.roles) {
            setIsServiceExecutive(
                userData.data.roles.some((r) => r.roleId === "SERVICE-EXECUTIVE"),
            );
            setIsDispatchManager(
                userData.data.roles.some((r) => r.roleId === "DISPATCH"),
            );
            setIsFinanceExecutive(
                userData.data.roles.some((r) => r.roleId === "FI-EXECUTIVE"),
            );
            setCurrentUserName(
                userData.data.firstName ||
                userData.data.name ||
                userData.data.userId ||
                "",
            );
        }
    }, [userData]);

    // ── UI state ────────────────────────────────────────────────────────────────
    const [isVisible, setIsVisible] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null);

    // ── Failure modal (replaces browser alert) ──────────────────────────────────
    const [showFailureModal, setShowFailureModal] = useState(false);
    const [errorFields, setErrorFields] = useState([]);
    const failureModalData = { title: "Validation Error" };

    // ── Add-form state ──────────────────────────────────────────────────────────
    const emptyForm = { tripType: tripType || "", slabIndex: "", manualRate: "", distanceKm: "" };
    const [form, setForm] = useState(emptyForm);

    // ── Unsaved rows (local only — never touches travelHistory prop) ─────────────
    const [newRows, setNewRows] = useState([]);

    // Clear unsaved rows when parent reloads data from DB after a successful save
    // We compare length: if travelHistory grew, the save was successful
    const prevLengthRef = React.useRef(travelHistory.length);
    useEffect(() => {
        if (travelHistory.length > prevLengthRef.current) {
            setNewRows([]);
        }
        prevLengthRef.current = travelHistory.length;
    }, [travelHistory]);

    // Notify parent whenever unsaved row count changes
    useEffect(() => {
        onUnsavedRowsChange?.(newRows.length > 0);
    }, [newRows, onUnsavedRowsChange]);


    // ── Derived values ──────────────────────────────────────────────────────────
    const selectedSlab =
        form.slabIndex !== "" ? RATE_SLABS[parseInt(form.slabIndex)] : null;

    const effectiveRate =
        selectedSlab?.type === "manual"
            ? parseFloat(form.manualRate) || 0
            : parseFloat(selectedSlab?.value) || 0;

    const liveTotal = calcTotal(effectiveRate, form.distanceKm);
    const allRows = [...travelHistory, ...newRows];

    // ── Add form handlers ───────────────────────────────────────────────────────
    const handleTripTypeChange = (e) =>
        setForm((p) => ({ ...p, tripType: e.target.value }));
    const handleSlabChange = (e) =>
        setForm((p) => ({ ...p, slabIndex: e.target.value, manualRate: "" }));
    const handleManualRateChange = (e) =>
        setForm((p) => ({ ...p, manualRate: e.target.value }));
    const handleDistanceChange = (e) =>
        setForm((p) => ({ ...p, distanceKm: e.target.value }));

    const handleAddRow = () => {
        const errors = [];
        if (!form.tripType) errors.push("Please select a Trip Type");
        if (form.slabIndex === "") errors.push("Please select a Rate Slab");
        if (selectedSlab?.type === "manual" && !form.manualRate)
            errors.push("Please enter the Rate per km");
        if (!form.distanceKm || parseFloat(form.distanceKm) <= 0)
            errors.push("Please enter Distance (km)");

        if (errors.length > 0) {
            setErrorFields(errors);
            setShowFailureModal(true);
            return;
        }

        const row = {
            local_id: `travel-${Date.now()}`,
            tripType: form.tripType,
            travelDate: new Date().toISOString().split("T")[0],
            rateSlabLabel: selectedSlab?.label ?? "",
            rateSlabType: selectedSlab?.type ?? "",
            rateSlab: effectiveRate,
            distanceKm: parseFloat(form.distanceKm),
            totalAmount: liveTotal,
            addedBy: currentUserName,
            seConfirmation: { confirmed: false, confirmedBy: "", confirmedAt: "", remarks: "" },
            dispatchConfirmation: { confirmed: false, confirmedBy: "", confirmedAt: "", remarks: "" },
            financeConfirmation: { confirmed: false, confirmedBy: "", confirmedAt: "", remarks: "" },
        };

        setNewRows((prev) => [...prev, row]);
        setForm(emptyForm);
    };

    // ── Delete unsaved row (pure local — no API, no parent notification) ─────────
    const handleDeleteNewRow = (localId) => {
        setNewRows((prev) => prev.filter((r) => r.local_id !== localId));
    };

    // ── Save unsaved rows — calls parent's onSave, clears newRows on success ────
    const handleSaveRows = async () => {
        if (newRows.length === 0) return;
        setSaving(true);
        try {
            // Strip local_id and auto-confirm SE stage — saving IS the SE confirmation
            const rowsToSave = newRows.map(({ local_id, ...row }) => ({
                ...row,
                seConfirmation: {
                    confirmed: true,
                    confirmedBy: currentUserName,
                    confirmedAt: new Date().toISOString(),
                    remarks: "Confirmed on save",
                },
            }));
            await onSave?.(rowsToSave);
            // Parent calls fetchData() after save → travelHistory prop grows →
            // the useEffect above clears newRows automatically.
            // We also clear here as a safety net in case parent doesn't reload.
            setNewRows([]);
        } catch {
            // Parent already showed error toast — keep newRows so user doesn't lose data
        } finally {
            setSaving(false);
        }
    };

    // ── Confirm modal ───────────────────────────────────────────────────────────
    const openConfirm = (rowId, stage) =>
        setConfirmModal({ rowId, stage, remarks: "" });

    const closeConfirm = () => setConfirmModal(null);

    const handleConfirmSubmit = () => {
        if (!confirmModal) return;
        onConfirmRow?.(confirmModal.rowId, confirmModal.stage, {
            remarks: confirmModal.remarks,
        });
        closeConfirm();
    };

    // ── Action cell renderer ────────────────────────────────────────────────────
    const renderAction = (row) => {
        // View-only mode — no actions at all
        if (disabled) return null;

        // Unsaved row — show delete button (no API call needed)
        if (row.local_id)
            return (
                <button
                    className="btn btn-danger btn-sm mt-1"
                    title="Delete entry"
                    onClick={() => handleDeleteNewRow(row.local_id)}
                >
                    <i className="bi bi-trash" />
                </button>
            );

        const next = getNextStage(row);
        if (!next) return <span className="badge bg-success">✓ Complete</span>;

        const canAct =
            (next === "se" && isServiceExecutive) ||
            (next === "dispatch" && isDispatchManager) ||
            (next === "finance" && isFinanceExecutive);

        if (!canAct) return <span className="text-muted small">—</span>;

        const labels = { se: "Submit", dispatch: "Approve", finance: "Mark Paid" };

        return (
            <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => openConfirm(row._id, next)}
            >
                {labels[next]}
            </button>
        );
    };

    // ── Table columns ───────────────────────────────────────────────────────────
    const columns = [
        { name: "Date", width: "135px", cell: (row) => <ReadCell value={row.travelDate} /> },
        { name: "Trip Type", width: "135px", cell: (row) => <ReadCell value={row.tripType} /> },
        { name: "Rate Slab", width: "110px", cell: (row) => <ReadCell value={row.rateSlabLabel} /> },
        { name: "Rate/km", width: "90px", cell: (row) => <ReadCell value={row.rateSlab} /> },
        { name: "Distance (km)", width: "120px", cell: (row) => <ReadCell value={row.distanceKm} /> },
        {
            name: "Total",
            width: "90px",
            cell: (row) => (
                <span className="fw-bold">
                    {row.totalAmount ?? 0}
                </span>
            ),
        },
        { name: "Added By", width: "130px", cell: (row) => <ReadCell value={row.addedBy} /> },
        { name: "Status", width: "150px", cell: (row) => getStatusBadge(row) },
        { name: "Action", width: "120px", cell: (row) => renderAction(row) },
    ];

    // Add button visible only when: not disabled + SE role + ERF is "Under Service Executive"--Changed to allow dispatch manager also adding the travel distance
    const canAdd = !disabled && (isServiceExecutive || isDispatchManager) && ["USE", "REUSE"].includes(erfStatusCode);

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="accordion mb-4" id="accordionTravelSection">
                <div className="accordion-item">

                    {/* ── Accordion Header ── */}
                    <h2 className="accordion-header">
                        <Row className="accordion-button">
                            <Col xs={1} className="text-left arrowBtn">
                                <i
                                    className={`bi ${isVisible ? "bi-chevron-down" : "bi-chevron-right"}`}
                                    onClick={() => setIsVisible((v) => !v)}
                                />
                            </Col>
                            <Col xs={1} className="text-center logoBtn">
                                <span className="iconLogo">DI</span>
                            </Col>
                            <Col xs={10} md={8}>
                                <span className="headerTitle">Distance Coverage Information</span>
                                <span className="subHeaderTitle">
                                    Service executive travel tracking &amp; distance coverage
                                </span>
                            </Col>
                        </Row>
                    </h2>

                    {/* ── Accordion Body — collapses correctly ── */}
                    <div className={`accordion-collapse collapse show ${isVisible ? "visible" : ""}`}>
                        <div className="accordion-body">

                            {/* ── Add Entry Form — SE only, status must be USE ── */}
                            {canAdd && (
                                <div className="mb-4">
                                    <Row className="bg-light p-2 border rounded-top align-items-center">
                                        <Col xs={12} className="commTopButtonRightLeftPadding">
                                            <h6 className="fw-bold mb-0 text-primary ps-2">
                                                Add Distance Entry
                                            </h6>
                                        </Col>
                                    </Row>
                                    <div className="p-3 border border-top-0 rounded-bottom">
                                        <Row className="align-items-end g-2">

                                            {/* Trip Type — hidden when pre-filled via prop */}
                                            {!tripType && (
                                                <Col xs={12} md={3} lg={2} className="commTopButtonRightLeftPadding">
                                                    <label className="form-label fw-medium mb-1">
                                                        Trip Type <span className="text-danger">*</span>
                                                    </label>
                                                    <select
                                                        className="form-select form-select-sm inputBox"
                                                        value={form.tripType}
                                                        onChange={handleTripTypeChange}
                                                    >
                                                        <option value="">Select</option>
                                                        {TRIP_TYPES.map((t) => (
                                                            <option key={t} value={t}>{t}</option>
                                                        ))}
                                                    </select>
                                                </Col>
                                            )}

                                            {/* Rate Slab */}
                                            <Col xs={12} md={3} lg={2} className="commTopButtonRightLeftPadding">
                                                <label className="form-label fw-medium mb-1">
                                                    Rate Slab <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    className="form-select form-select-sm inputBox"
                                                    value={form.slabIndex}
                                                    onChange={handleSlabChange}
                                                >
                                                    <option value="">Select</option>
                                                    {RATE_SLABS.map((s, i) => (
                                                        <option key={i} value={i}>{s.label}</option>
                                                    ))}
                                                </select>
                                            </Col>

                                            {/* Manual rate — shown only when Manual slab selected */}
                                            {selectedSlab?.type === "manual" && (
                                                <Col xs={6} md={2} lg={2} className="commTopButtonRightLeftPadding">
                                                    <label className="form-label fw-medium mb-1">
                                                        Rate/km <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        className="form-control form-control-sm inputBox"
                                                        type="number"
                                                        min="0"
                                                        step="0.1"
                                                        value={form.manualRate}
                                                        onChange={handleManualRateChange}
                                                    />
                                                </Col>
                                            )}

                                            {/* Distance */}
                                            <Col xs={6} md={2} lg={2} className="commTopButtonRightLeftPadding">
                                                <label className="form-label fw-medium mb-1">
                                                    Distance (km) <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    className="form-control form-control-sm inputBox"
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    value={form.distanceKm}
                                                    onChange={handleDistanceChange}
                                                />
                                            </Col>

                                            {/* Live total — read-only calculated field */}
                                            <Col xs={6} md={2} lg={2} className="commTopButtonRightLeftPadding">
                                                <label className="form-label fw-medium mb-1">
                                                    Total
                                                </label>
                                                <input
                                                    className="form-control form-control-sm inputBox"
                                                    value={liveTotal > 0 ? liveTotal : ""}
                                                    disabled
                                                    readOnly
                                                    style={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}
                                                />
                                            </Col>

                                            {/* Add button */}
                                            <Col xs={12} md={2} lg={2} className="commTopButtonRightLeftPadding">
                                                <button className="btnTable" onClick={handleAddRow}>
                                                    <i className="bi bi-plus-lg" /> Add
                                                </button>
                                            </Col>

                                        </Row>
                                    </div>
                                </div>
                            )}

                            {/* ── Travel History Table ── */}
                            <div className="mb-2">
                                <Row className="bg-light p-2 border rounded-top align-items-center">
                                    <Col xs={8} className="commTopButtonRightLeftPadding">
                                        <h6 className="fw-bold mb-0 text-primary ps-2">
                                            Travel Distance History
                                        </h6>
                                    </Col>

                                    {/* Save button — appears only when there are unsaved rows */}
                                    {newRows.length > 0 && !disabled && (
                                        <Col xs={4} className="text-end pe-3">
                                            <button
                                                className="btnTable"
                                                onClick={handleSaveRows}
                                                disabled={saving}
                                            >
                                                <i className="bi bi-save me-1" />
                                                {saving ? "Saving..." : `Save`}
                                            </button>
                                        </Col>
                                    )}
                                </Row>

                                <DataTable
                                    columns={columns}
                                    data={allRows}
                                    customStyles={tableHeadStyle}
                                    noDataComponent={
                                        <div className="p-2 text-center text-muted">No Entries.</div>
                                    }
                                    pagination={allRows.length > 10}
                                />
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* ── Confirm Modal (SE submit / Dispatch approve / Finance mark paid) ── */}
            {confirmModal && (
                <div
                    className="modal d-block"
                    tabIndex={-1}
                    style={{ background: "rgba(0,0,0,0.4)" }}
                >
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header py-2">
                                <h6 className="modal-title mb-0">
                                    {{ se: "Submit Travel Entry", dispatch: "Approve Distance", finance: "Mark as Paid" }[confirmModal.stage]}
                                </h6>
                                <button type="button" className="btn-close" onClick={closeConfirm} />
                            </div>
                            <div className="modal-body py-2">
                                <label className="form-label fw-medium mb-1">
                                    Remarks
                                </label>
                                <textarea
                                    className="form-control form-control-sm"
                                    rows={2}
                                    value={confirmModal.remarks}
                                    onChange={(e) =>
                                        setConfirmModal((p) => ({ ...p, remarks: e.target.value }))
                                    }
                                />
                            </div>
                            <div className="modal-footer py-2">
                                <button className="btnTable btn-danger text-white" onClick={closeConfirm}>
                                    Cancel
                                </button>
                                <button className="btnTable" onClick={handleConfirmSubmit}>
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Validation failure modal (replaces browser alert) ── */}
            <FailureModal
                show={showFailureModal}
                onHide={() => setShowFailureModal(false)}
                failuremodaldata={failureModalData}
                requiredfields={errorFields}
            />
        </>
    );
};

export default ERFTravelSection;